import React from "react";
import { Row, Col } from "react-flexbox-grid";
import { Redirect, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Normalize } from "styled-normalize";

import BaseStyle from "../../styles/baseStyles";
import urlPatterns from "../../urls";
import { isLoggedIn } from "../../helpers/auth";
import { getGuestCheckoutLoginRedirect } from "../../helpers/guestCheckoutLoginRedirect";
import { isCapacitorNative } from "../../helpers/isCapacitorNative";
import { usePreloadIterable } from "../../helpers/usePreloadIterable";
import breakpoints from "../../styles/breakpoints";

import { Container } from "../../components/atoms/Container";
import { Section } from "../../components/atoms/Section";
import Title from "../../components/atoms/Title";
import PageMeta from "../../components/molecules/PageMeta";
import SignUpSocial from "../../components/molecules/SignUpSocial";
import LoginFormRequestOTP from "../../components/molecules/LoginFormRequestOTP";
import LogInHeader from "../../components/organisms/LogInHeader";
import FullscreenLayout from "../../components/organisms/FullscreenLayout";
import heroImage from "../../assets/images/temp/GPD_dancing_man_bw.svg";
import CountryDropdown from "../../components/atoms/CountryDropdown";
import { COUNTRY_SPECIFIC_SETTINGS, I18N_NAMESPACES } from "../../helpers/constants";
import { getCountry } from "../../helpers/urls.helper";
import { colors } from "../../styles/variables";

const LogInWrapper = styled.div`
  min-height: 100vh;
  overflow: hidden;
  background-color: ${colors.blue};
`;

const ContainerSmall = styled(Container)`
  position: relative;
  ${breakpoints.mdUp} {
    max-width: 745px;
  }
`;

const Holder = styled.div`
  position: relative;
  z-index: 20;
  padding-bottom: 40px;
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 130px;
  position: absolute;
  right: 50px;
  z-index: -1;
  bottom: -6px;
  z-index: -1;
  ${breakpoints.smDown} {
    max-width: 100px;
    right: 20px;
  }
`;

const LogInFooter = styled.div`
  position: relative;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  ${breakpoints.smDown} {
    flex-direction: column;
  }
`;

const CountryDropdownWrapper = styled.div`
  min-width: 150px;
  position: absolute;
  margin-top: 14px;
  top: 0;
  right: 0;
  ${breakpoints.smDown} {
    position: relative;
  }
`;

interface LogInProps {
  handleClosePanel?: () => void;
}

const LogInRequestOTP = ({ handleClosePanel }: LogInProps) => {
  // Preload the @iterable/web-sdk chunk in the background while the user
  // is filling out the form. The chunk is needed for the post-login
  // initializeWithConfig in Layout — fetching it now removes the
  // download latency from the post-login critical path and prevents
  // identifyIterableUser racing against an uninitialised SDK.
  usePreloadIterable();

  const location = useLocation<{
    email?: string;
    redirectUrl?: string;
    quiz?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    quizData?: any;
    isShoppingCart?: boolean;
    isGiftRedeem?: boolean;
  }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changeCountry = (countryId: any) => {
    window.location.href = `/${countryId}/login/`;
  };

  const country = getCountry();
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);

  if (isLoggedIn()) {
    return (
      <Redirect to={getGuestCheckoutLoginRedirect(urlPatterns.MY_DASHBOARD, !!location.state?.quizData)} />
    );
  }

  return (
    <>
      <PageMeta
        title={t("common:pageMetaTitle", {
          title: t("common:pageMetaTitles.login"),
        })}
      />
      <Normalize />
      <BaseStyle />

      <FullscreenLayout topBg={colors.blue}>
        <LogInWrapper>
          <LogInHeader
            handleClosePanel={handleClosePanel}
            backButtonLink={isCapacitorNative() ? urlPatterns.WELCOME : urlPatterns.HOME}
          />

          <Section>
            <ContainerSmall>
              <Holder>
                <HeroImage src={heroImage} alt="heroImage" />
                <Row>
                  <Col xs={11} lg={9}>
                    <Title tag={1} uppercase>
                      {`${t("common:loginPage.heroTitle")}`}
                    </Title>
                  </Col>
                </Row>
              </Holder>

              <LoginFormRequestOTP />

              <LogInFooter>
                <SignUpSocial
                  // @ts-ignore - withRouter strips prop types
                  title={t("common:loginPage.signUpSocialTitle")}
                  text={t("common:loginPage.signUpSocialText")}
                  linkText={t("common:loginPage.signUpSocialLinkText")}
                  linkPath={urlPatterns.SIGN_UP}
                  redirectUrl={location?.state?.redirectUrl}
                  quizData={location?.state?.quizData}
                />
                <CountryDropdownWrapper>
                  <CountryDropdown
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onSelectCountry={(value: any) => changeCountry(value)}
                    renderCountryLabel={(countryId, selected) => {
                      const { FLAG, NAME } = COUNTRY_SPECIFIC_SETTINGS[countryId] || {};

                      if (selected) {
                        return `${FLAG} ${NAME}`.toUpperCase();
                      }
                      return `${FLAG} ${NAME}`;
                    }}
                    selectedCountry={country}
                  />
                </CountryDropdownWrapper>
              </LogInFooter>
            </ContainerSmall>
          </Section>
        </LogInWrapper>
      </FullscreenLayout>
    </>
  );
};

export default LogInRequestOTP;
